import { AccountUser, Member, MemberRole, MemberStatus, User, UserRole } from '@quick/client';
import { useProfileQuery } from '../../hooks/Profile';
import { useSelector } from 'react-redux';
import { ReduxState } from '../reducers';
import deepEqual from 'react-fast-compare';
import { useLocation } from 'react-router-dom';
import { useCommunityPrefixPath } from '../../hooks/Routing';
import { AuthState } from '../reducers/Auth';
import { DocumentState } from '../reducers/Document';

export const useAuthState = () => {
  return useSelector<ReduxState, AuthState>((state) => state.auth, deepEqual);
};

/**
 * TODO(zhixuan): move these methods to hooks/Profile.ts
 */
export const useIsAdmin = () => {
  const currentUser = useCurrentUser();
  const currentMember = useCurrentMember();
  return (
    currentUser?.role === UserRole.Admin ||
    currentMember?.role === MemberRole.BoardMember ||
    currentMember?.role === MemberRole.Manager
  );
};

export const useCurrentUser = (): User | undefined => {
  const profileQuery = useProfileQuery();
  return profileQuery.data?.user;
};

export const useCurrentMember = (): Member | undefined => {
  const profileQuery = useProfileQuery();
  const currentCommunityId = useCurrentCommunityId();
  if (!profileQuery.data) {
    return undefined;
  }
  if (currentCommunityId) {
    return profileQuery.data.members.find((it) => it.communityId === currentCommunityId);
  }
  return undefined;
};

export const useCurrentActiveMember = (): Member | undefined => {
  const member = useCurrentMember();
  if (member?.status !== MemberStatus.Active) {
    return undefined;
  }
  return member;
};

export const useMyMembers = (): Member[] => {
  const profileQuery = useProfileQuery();
  return profileQuery.data?.members ?? [];
};

export const useCurrentCommunityId = (): number | undefined => {
  // https://9oelm.github.io/2020-09-13--How-to-make-useSelector-not-a-disaster/
  return useSelector<ReduxState, number | undefined>((state) => state.user.currentCommunityId, deepEqual);
};

export const useCurrentAccountUser = (): AccountUser | undefined => {
  const vendorLinkAccountUser = useVendorlinkAccountUser();
  // Community VendorLink integration.
  const currentMember = useCurrentMember();
  const communityEmbeddedAccountUser = useCommunityEmbeddedAccountUser();
  if (currentMember) {
    return communityEmbeddedAccountUser;
  }
  return vendorLinkAccountUser;
};

export const useMyAccountUsers = (): AccountUser[] => {
  const profileQuery = useProfileQuery();
  return profileQuery.data?.accountUsers ?? [];
};

export const useCurrentAccountId = (): number | undefined => {
  // https://9oelm.github.io/2020-09-13--How-to-make-useSelector-not-a-disaster/
  const currentAccountId = useSelector<ReduxState, number | undefined>(
    (state) => state.user.currentAccountId,
    deepEqual
  );
  // Community VendorLink integration.
  const currentMember = useCurrentMember();
  const communityEmbeddedAccountUser = useCommunityEmbeddedAccountUser();
  if (currentMember) {
    return communityEmbeddedAccountUser?.accountId;
  }
  return currentAccountId;
};

const useCommunityEmbeddedAccountUser = () => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentMember = useCurrentMember();
  const location = useLocation();
  if (!currentMember) {
    return undefined;
  }
  if (location.pathname.startsWith(`${appPrefixPath}/myUnit`)) {
    return currentMember.propertyVendorlinkAccountUser;
  }
  if (location.pathname.startsWith(`${appPrefixPath}/treasury`)) {
    return currentMember.communityVendorlinkAccountUser;
  }
  return undefined;
};

export const useIsInMyUnitRoute = (): boolean => {
  const appPrefixPath = useCommunityPrefixPath();
  const currentMember = useCurrentMember();
  const location = useLocation();
  if (!currentMember) {
    return false;
  }
  return location.pathname.startsWith(`${appPrefixPath}/myUnit`);
};

const useVendorlinkAccountUser = () => {
  const profileQuery = useProfileQuery();
  const currentAccountId = useCurrentAccountId();
  if (!profileQuery.data) {
    return undefined;
  }
  if (currentAccountId) {
    return profileQuery.data.accountUsers.find((it) => it.accountId === currentAccountId);
  }
  if (profileQuery.data.accountUsers.length > 0) {
    return profileQuery.data.accountUsers[0];
  }
  return undefined;
};

export const useDocumentState = () => {
  return useSelector<ReduxState, DocumentState>((state) => state.document);
};
